import React, { useState, useEffect, useRef } from 'react';
import { Upload, Download, Eye, Trash2, FileText, FileImage, FileVideo, FileAudio, FileArchive, FileCode } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { formatFileSize, getFileTypeBadge } from '../../utils/timelineMetadata';
import { JobContactAttachment } from '../../types';
import { apiService } from '../../services/api';

interface FilesTabProps {
  attachments: JobContactAttachment[];
  onUpload: () => void;
  onDownload: (attachment: JobContactAttachment) => void;
  onPreview: (attachment: JobContactAttachment) => void;
  onDelete: (attachment: JobContactAttachment) => void;
}

const FilesTab: React.FC<FilesTabProps> = ({
  attachments,
  onUpload,
  onDownload,
  onPreview,
  onDelete,
}) => {
  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
      case 'svg':
        return { Icon: FileImage, className: 'text-green-500' };
      case 'mp4':
      case 'm4v':
      case 'avi':
      case 'mov':
      case 'webm':
      case 'wmv':
      case 'flv':
        return { Icon: FileVideo, className: 'text-purple-500' };
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'aac':
        return { Icon: FileAudio, className: 'text-blue-500' };
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return { Icon: FileArchive, className: 'text-orange-500' };
      case 'js':
      case 'ts':
      case 'html':
      case 'css':
      case 'json':
      case 'xml':
        return { Icon: FileCode, className: 'text-indigo-500' };
      default:
        return { Icon: FileText, className: 'text-gray-500' };
    }
  };

  const isImageFile = (fileName: string): boolean => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  const formatUploadDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Image Thumbnail Component
  const ImageThumbnail: React.FC<{ filePath: string; fileName: string }> = ({ filePath, fileName }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const hasRequestedRef = useRef(false);

    useEffect(() => {
      let isMounted = true;
      let observer: IntersectionObserver | null = null;

      const loadImage = async () => {
        try {
          const blob = await apiService.downloadFile(filePath);
          if (!isMounted) return;
          const url = window.URL.createObjectURL(blob);
          setImageUrl(url);
          setLoading(false);
        } catch (err) {
          if (isMounted) {
            setError(true);
            setLoading(false);
          }
        }
      };

      const observe = () => {
        if (!containerRef.current || hasRequestedRef.current) return;
        observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && !hasRequestedRef.current) {
            hasRequestedRef.current = true;
            loadImage();
            observer && observer.disconnect();
          }
        }, { rootMargin: '200px' });
        observer.observe(containerRef.current);
      };

      observe();

      return () => {
        isMounted = false;
        if (observer) observer.disconnect();
        if (imageUrl) {
          window.URL.revokeObjectURL(imageUrl);
        }
      };
    }, [filePath]);

    if (loading) {
      return (
        <div ref={containerRef} className="w-20 h-20 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
          <div className="animate-pulse text-xs text-gray-400">...</div>
        </div>
      );
    }

    if (error || !imageUrl) {
      return (
        <div className="w-20 h-20 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
          <FileImage className="h-8 w-8 text-gray-400" />
        </div>
      );
    }

    return (
      <img
        src={imageUrl}
        alt={fileName}
        className="w-20 h-20 object-cover rounded-lg"
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with Upload Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">File Attachments</h2>
        <Button
          onClick={onUpload}
          className="flex items-center space-x-2 bg-accent-color hover:bg-accent-color-600 text-white"
        >
          <Upload className="h-4 w-4" />
          <span>Upload Files</span>
        </Button>
      </div>

      {/* Files List */}
      <Card>
        <CardContent className="p-6">
          {attachments.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <div className="text-sm">No files uploaded yet</div>
              <div className="text-xs">Upload files to share with the team</div>
            </div>
          ) : (
            <div className="space-y-3">
              {attachments.map((attachment) => {
                const { Icon, className } = getFileIcon(attachment.fileName);
                const { badge, color } = getFileTypeBadge(attachment.fileName);
                const isImage = isImageFile(attachment.fileName);

                return (
                  <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      {/* Thumbnail or Icon */}
                      {isImage ? (
                        <div className="relative flex-shrink-0">
                          <ImageThumbnail filePath={attachment.filePath} fileName={attachment.fileName} />
                          {/* File Type Badge */}
                          <div className="absolute -top-1 -right-1">
                            <span className={`text-xs font-bold px-1 py-0.5 rounded ${color} shadow-sm`}>
                              {badge}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="relative flex-shrink-0">
                          <Icon className={`h-10 w-10 ${className}`} />
                          {/* File Type Badge */}
                          <div className="absolute -top-1 -right-1">
                            <span className={`text-xs font-bold px-1 py-0.5 rounded ${color} shadow-sm`}>
                              {badge}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {attachment.fileName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatFileSize(attachment.fileSize)} • {formatUploadDate(attachment.uploadedDate)}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onPreview(attachment)}
                        className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                        title="Preview"
                      >
                        <Eye className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={() => onDownload(attachment)}
                        className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                        title="Download"
                      >
                        <Download className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={() => onDelete(attachment)}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FilesTab;
